import { useMutation } from "@tanstack/react-query";
import i18next from "i18next";
import { ApiFortAgentTransferSign } from "@/api";
import {
  FortAgentTransferResultCode,
  type FortAgentTransferSignRequest,
} from "@/api/types";
import { showError } from "@/lib/toast";
import { getTokenName } from "@/config/common";
import { CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";

export function useFortAgentTransferSign() {
  return useMutation({
    mutationFn: async (data: FortAgentTransferSignRequest) => {
      const res = await ApiFortAgentTransferSign(data);
      return res.data;
    },
    onSuccess: data => {
      const code = data.resultCode;
      if (code === FortAgentTransferResultCode.Success) return;

      if (code === FortAgentTransferResultCode.SignServiceRejected) {
        showError(
          i18next.t("toast.fortAgent.transfer.result.signServiceRejected", {
            tokenName: getTokenName(CCC_FUSD_ADDRESS),
            tokenNameUsdt: getTokenName(USDT_ADDRESS),
            detail: data.resultMsg || "",
          })
        );
        return;
      }

      const key =
        FORT_AGENT_TRANSFER_RESULT_I18N_KEY[
          code as keyof typeof FORT_AGENT_TRANSFER_RESULT_I18N_KEY
        ] ?? "toast.fortAgent.transfer.result.unknown";

      showError(
        i18next.t(key, {
          tokenName: getTokenName(CCC_FUSD_ADDRESS),
          tokenNameUsdt: getTokenName(USDT_ADDRESS),
        })
      );
    },
    onError: () => {},
  });
}

/** 与 {@link FortAgentTransferResultCode} 一一对应的 i18n key（`toast.fortAgent.transfer.result.*`） */
export const FORT_AGENT_TRANSFER_RESULT_I18N_KEY: Record<
  Exclude<FortAgentTransferResultCode, FortAgentTransferResultCode.Success>,
  string
> = {
  [FortAgentTransferResultCode.WalletNotBound]:
    "toast.fortAgent.transfer.result.walletNotBound",
  [FortAgentTransferResultCode.InvalidAgentId]:
    "toast.fortAgent.transfer.result.invalidAgentId",
  [FortAgentTransferResultCode.InvalidRecipientAddress]:
    "toast.fortAgent.transfer.result.invalidRecipientAddress",
  [FortAgentTransferResultCode.NotOwnerOrAgentMissing]:
    "toast.fortAgent.transfer.result.notOwnerOrAgentMissing",
  [FortAgentTransferResultCode.SignServiceRejected]:
    "toast.fortAgent.transfer.result.signServiceRejected",
  [FortAgentTransferResultCode.ActivatedCannotTransfer]:
    "toast.fortAgent.transfer.result.activatedCannotTransfer",
  [FortAgentTransferResultCode.InsufficientFusd]:
    "toast.fortAgent.transfer.result.insufficientFusd",
  [FortAgentTransferResultCode.CannotTransferToSelf]:
    "toast.fortAgent.transfer.result.cannotTransferToSelf",
  [FortAgentTransferResultCode.PendingAgentOperation]:
    "toast.fortAgent.transfer.result.pendingAgentOperation",
};
